import { traverse, anyObj } from './../utils/traverse'
import { Operation } from './WPSClient';
type codigoArtifacts = { '*className*'?: string, '*classHierarchy*'?: string[] }

export const dePerl = (object: object): any => traverse(object, (o: codigoArtifacts & anyObj) => {
    const className = o['*className*']
    if (className) {
        const namespace = className.split(/[\.#]/);
        const coralShape = namespace.pop();
        o['__type'] = `${namespace.join('.')}#${coralShape}`;
        delete o['*className*']
    }
    delete o['*classHierarchy*']
    return o
})

export const operationToCamelCase = (op: Operation) => op[0].toLowerCase() + op.slice(1);

export const safeBool = (thing: '1' | '0' | 0 | 1 | true | false | undefined) => thing == true

export const isCustomEventSupported = (): boolean => {
    try {
        const event = new CustomEvent('test', { detail: 'supported' });
        return event.detail === 'supported';
    } catch (e) {
        return false;
    }
}

/**
 * Extracts availability metadata from the response and appends it to the locals object that is scoped to the request
 * @param responseBody Response body retrieved from downstream call as JSON
 * @param locals An object that contains response local variables scoped to the request
 */
export function processMetadata(responseBody: any, locals: any): void {
    if (!locals) {
        return;
    }

    const metadata = extractMetadata(responseBody);
    if (!metadata) {
        return;
    }

    recordAvailabilityMetadata(metadata.availability.severity, metadata.availability.description, locals);
}

/**
 * Records the given availability metadata in an object scoped to the request
 * @param severity Severity of the availability metadata
 * @param description Description of the availability metadata
 * @param locals An object that contains response local variables scoped to the request
 */
export function recordAvailabilityMetadata(severity: Severity, description: string, locals: any): void {
    if (!locals) {
        return;
    }

    initializeAvailabilityData(locals);
    locals.availability.descriptions.push(description);
    switch(severity) {
        case 'NoDegradation':
            // Initial severity is set to NoDegredation
            break;
        case 'MinorDegradation':
            if (locals.availability.severity !== 'SignificantlyDegraded' ||
                locals.availability.severity !== 'Degraded') {
                locals.availability.severity = 'MinorDegradation';
            }
            break;
        case 'Degraded':
            if (locals.availability.severity !== 'SignificantlyDegraded') {
                locals.availability.severity = 'Degraded';
            }
            break;
        case 'SignificantlyDegraded':
            locals.availability.severity = 'SignificantlyDegraded';
            break;
        default:
            if (locals.availability.severity === 'NoDegradation') {
                locals.availability.severity = 'UnknownAvailabilitySeverity';
            }
            break;                                 
    }
}

export function extractMetadata(response: object | string): Metadata {
    const maybeObj = response as any;
    const valid: boolean = maybeObj &&
        maybeObj.metadata &&
        maybeObj.metadata.availability &&
        maybeObj.metadata.availability.severity &&
        maybeObj.metadata.availability.description;

    return valid ?
        {
            availability: {
                severity: maybeObj.metadata.availability.severity,
                description: maybeObj.metadata.availability.description,
            }
        } :
        null as any;
}

function initializeAvailabilityData(locals: any): void {
    if (locals.availability) {
        return;
    }

    locals.availability = {
        severity: 'NoDegradation',
        descriptions: [],
    };
}

export interface Metadata {
    availability: AvailabilityMetadata;
}

export interface AvailabilityMetadata {
    severity: Severity;
    description: string;
}

export type Severity = 'NoDegradation' | 'MinorDegradation' | 'Degraded' | 'SignificantlyDegraded';

export class UnauthorizedError extends Error {
    constructor(msg: string) {
        super(msg);
        this.name = 'UnauthorizedError';
        Object.setPrototypeOf(this, UnauthorizedError.prototype);
    }
}

export class ForbiddenError extends Error {
    constructor(msg: string) {
        super(msg);
        this.name = 'ForbiddenError';
        Object.setPrototypeOf(this, ForbiddenError.prototype);
    }
}

export class FailedRequestError extends Error {
    constructor(msg: string) {
        super(msg);
        this.name = 'FailedRequestError';
        Object.setPrototypeOf(this, FailedRequestError.prototype);
    }
}

export class JSONParseError extends Error {
    constructor(msg: string) {
        super(msg);
        this.name = 'JSONParseError';
        Object.setPrototypeOf(this, JSONParseError.prototype);
    }
}

export class NoStatusCodeError extends Error {
    constructor(msg: string) {
        super(msg);
        this.name = 'NoStatusCodeError';
        Object.setPrototypeOf(this, NoStatusCodeError.prototype);
    }
}